import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { StatusTag } from 'web-ui-components/lib/typography/tags';
import { Identifier } from 'web-ui-components/lib/typography/modifiers';
import { DataTable } from 'web-ui-components/lib/molecules/tables';
import { Card } from 'web-ui-components/lib/organisms/cards';
import Decline, { Cancel } from '../Decline/Decline';
import { getMyPaymentsToAuthorise } from '../../states/payments/builders';
import { getMyPaymentsSortColumn } from '../../states/payments/selectors';
import {
	showMyBatch,
	hideMyBatch,
	selectToAuthorise,
	selectToView,
	updateMyPaymentsSortColumn,
} from '../../states/payments/actions';
import UserOptions from './UserOptions';
import { declinePayment, cancelPayment } from '../../states/page/actions';
import { editByIdPaymentTypes } from '../../config';
import { showAuthoriseLink } from '../../utils/paymentsUtils';
import { REVIEWED, HARD, SOFT, brands } from '../../constants/Constants';
import { calcPaymentTypeCapable } from '../../states/capabilities/selectors';
import { getBankOrBrandId } from '../../states/envConfig/selectors';

const PreferredMySortColumn = 'PreferredMySortColumn';

const mapState = (state) => ({
	isPaymentTypeCapable: (paymentType) => calcPaymentTypeCapable(state, paymentType),
	paymentsToAuthorise: getMyPaymentsToAuthorise(state),
	sortColumn: getMyPaymentsSortColumn(state),
	brandId: getBankOrBrandId(state),
});

const mapDispatch = (dispatch) => ({
	updateSortColumn: (sortColumn) => {
		dispatch(updateMyPaymentsSortColumn(sortColumn));
	},
	handleViewAll: (batchid) => dispatch(showMyBatch(batchid)),
	handleHideAll: (batchid) => dispatch(hideMyBatch(batchid)),
	handleAuthorise: (paymentId) => dispatch(selectToAuthorise(paymentId)),
	handleView: (paymentId) => dispatch(selectToView(paymentId)),
	handleDecline: (paymentId) => dispatch(declinePayment(paymentId)),
	handleCancel: (paymentId) => dispatch(cancelPayment(paymentId)),
});

const columns = [
	{
		title: 'Date',
		id: 'date',
		path: ['date'],
		sortDirection: 'asc',
	},
	{
		title: 'From',
		id: 'from',
		path: ['from'],
		sortDirection: 'asc',
	},
	{
		title: 'To',
		id: 'to',
		path: ['to'],
		sortDirection: 'asc',
	},
	{
		title: 'Amount',
		id: 'amount',
		path: ['amount'],
		sortDirection: 'asc',
	},
	{
		title: 'Reference',
		id: 'reference',
		path: ['formattedReference'],
		sortDirection: 'asc',
	},
	{
		title: 'Status',
		id: 'status',
		path: ['statusBadge'],
	},
	{
		title: 'Options',
		id: 'options',
		path: ['options'],
		isHiddenForPrint: true,
	},
];

const getSortedColumns = (selectedColumn) => {
	if (!selectedColumn) return columns;
	const sortedColumn = columns.filter((column) => column.id === selectedColumn.id);
	sortedColumn[0].sortDirection = selectedColumn.sortDirection;
	return columns;
};
class MyPaymentsToAuth extends Component {
	constructor(props) {
		super(props);
		this.state = {
			selectedPaymentToDecline: null,
			selectedPaymentToCancel: null,
		};

		this.decline = this.decline.bind(this);
		this.cancel = this.cancel.bind(this);
		this.close = this.close.bind(this);
		this.edit = this.edit.bind(this);
		this.sort = this.sort.bind(this);
	}

	decline(id) {
		this.setState({
			selectedPaymentToDecline: id,
			selectedPaymentToCancel: null,
		});
	}

	cancel(id) {
		this.setState({
			selectedPaymentToCancel: id,
			selectedPaymentToDecline: null,
		});
	}

	close() {
		this.setState({
			selectedPaymentToDecline: null,
			selectedPaymentToCancel: null,
		});
	}

	edit(payment) {
		const { handleEdit, handleEditById } = this.props;
		if (editByIdPaymentTypes.includes(payment.type)) {
			const paymentToEdit = {
				paymentId: payment.id,
				account: payment.fromAccountId,
			};
			return handleEditById(payment.type, paymentToEdit);
		}
		return handleEdit(payment.id);
	}

	sort(column) {
		const sortDirection = column.sortDirection === 'asc' ? 'desc' : 'asc';
		const sortByColumn = {
			...column,
			sortDirection,
		};
		this.props.updateSortColumn(sortByColumn);
		this.props.storeSortPreferences(PreferredMySortColumn, sortByColumn);
	}

	render() {
		const {
			paymentsToAuthorise,
			handleViewAll,
			handleHideAll,
			handleAuthorise,
			handleView,
			noPaymentsMsg,
			handleEdit,
			handleDecline,
			handleCancel,
			refresh,
			isPaymentTypeCapable,
			sortColumn,
			brandId,
		} = this.props;

		const sortedColumns = getSortedColumns(sortColumn);

		const getUserOptions = (payment) => {
			if (payment.isBatchLine) {
				return <UserOptions view handleView={() => handleView(payment.id)} id={payment.id} />;
			}
			const paymentTypeCapable = isPaymentTypeCapable(payment.type);
			const authorise = paymentTypeCapable && payment.paymentAuthType === HARD;
			const review = paymentTypeCapable && showAuthoriseLink(payment);
			const edit = paymentTypeCapable && payment.accountRights.create && !payment.file_upload;
			const cancel = payment.accountRights.create;
			const view = payment.paymentAuthType === SOFT;
			const decline = payment.paymentAuthType === HARD;
			const paymentUploadView = payment.paymentAuthType === SOFT && payment.status === REVIEWED;
			if (payment.isBatch || payment.file_upload) {
				return (
					<UserOptions
						viewAll={!payment.showBatch && !payment.file_upload}
						hideAll={!!payment.showBatch}
						authorise={authorise}
						review={review}
						edit={edit}
						decline={decline}
						cancel={cancel}
						paymentUploadView={paymentUploadView}
						handleAuthorise={() => handleAuthorise(payment.id)}
						handleDecline={() => this.decline(payment.id)}
						handleCancel={() => this.cancel(payment.id)}
						handleViewAll={() => handleViewAll(payment.id)}
						handleHideAll={() => handleHideAll(payment.id)}
						handleEdit={() => handleEdit(payment.id)}
						id={payment.id}
					/>
				);
			}

			return (
				<UserOptions
					view={view}
					authorise={authorise}
					edit={edit}
					decline={decline}
					cancel={cancel}
					handleAuthorise={() => handleAuthorise(payment.id)}
					handleDecline={() => this.decline(payment.id)}
					handleCancel={() => this.cancel(payment.id)}
					handleView={() => handleView(payment.id)}
					handleEdit={() => this.edit(payment)}
					id={payment.id}
				/>
			);
		};
		const referenceTruncateCharacterCount = brandId === brands.VM ? 6 : 16;
		const getFormattedReference = (reference) => (
			<Identifier.Truncated characterCount={referenceTruncateCharacterCount} value={reference} />
		);
		const getDataWithOptions = (payments) =>
			payments.map((payment) => ({
				...payment,
				from: payment.file_upload ? 'File upload summary' : payment.from,
				to: <Identifier.Breaking>{payment.to}</Identifier.Breaking>,
				statusBadge: <StatusTag.WarningMinor isFlush>Awaiting Authorisation</StatusTag.WarningMinor>,
				options: getUserOptions(payment),
				formattedReference: getFormattedReference(payment.reference),
			}));

		const renderData = () => {
			if (paymentsToAuthorise.length) {
				const data = getDataWithOptions(paymentsToAuthorise);
				return (
					<div>
						<DataTable
							layout="transactions"
							isBranded
							columns={sortedColumns}
							data={data}
							keyPath={['id']}
							onHeaderCellClick={this.sort}
						/>
						{this.state.selectedPaymentToDecline !== null && (
							<Decline
								handleClose={this.close}
								handleConfirm={() => handleDecline(this.state.selectedPaymentToDecline)}
								refresh={refresh}
							/>
						)}
						{this.state.selectedPaymentToCancel !== null && (
							<Cancel
								handleClose={this.close}
								handleConfirm={() => handleCancel(this.state.selectedPaymentToCancel)}
								refresh={refresh}
							/>
						)}
					</div>
				);
			}

			return (
				<div style={{ marginTop: '-1px' }}>
					<Card bordered>
						<p className="u-margin-bottom-0">{noPaymentsMsg}</p>
					</Card>
				</div>
			);
		};

		return renderData();
	}
}

MyPaymentsToAuth.propTypes = {
	isPaymentTypeCapable: PropTypes.func,
	paymentsToAuthorise: PropTypes.arrayOf(PropTypes.shape({})),
	noPaymentsMsg: PropTypes.string,
	updateSortColumn: PropTypes.func.isRequired,
	handleViewAll: PropTypes.func.isRequired,
	handleHideAll: PropTypes.func.isRequired,
	handleAuthorise: PropTypes.func.isRequired,
	handleEdit: PropTypes.func.isRequired,
	handleView: PropTypes.func.isRequired,
	sortColumn: PropTypes.shape({}),
	handleDecline: PropTypes.func.isRequired,
	refresh: PropTypes.func.isRequired,
	handleCancel: PropTypes.func.isRequired,
	handleEditById: PropTypes.func,
	brandId: PropTypes.string.isRequired,
	storeSortPreferences: PropTypes.func.isRequired,
};

export default connect(mapState, mapDispatch)(MyPaymentsToAuth);
